import type { RefObject } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useToast } from '../../platform/feedback/ToastProvider'
import { readOnlyNote } from './RuleRow'
import { WhenSentence } from './RuleSentence'
import { actionText, type Names } from './ruleText'
import type { ReadRule } from './ruleWire'
import { useDeleteRule, useUpdateRule } from './useRules'

/** Backend #447: delete no longer re-runs rules, so this is what happens. */
const BODY =
  "Transactions it already changed keep their category and tags. New transactions won't be filed by it."

/**
 * Delete a rule — or, for one that is running, pause it instead, which is
 * usually what a person hesitating over Delete wants.
 *
 * Mounted only while a rule is being deleted, like `DeleteMerchantDialog`.
 * `onClose` is "Keep it" (the builder underneath, if any, stays as it was);
 * `onDone` is a delete or a pause that succeeded, after which the caller
 * closes everything, the builder included — its edit is moot either way. It
 * says which: a screen showing the one rule leaves after a delete, not a pause.
 * A failure keeps the dialog open and says nothing changed.
 */
export function DeleteRuleDialog({
  read,
  names,
  onClose,
  onDone,
  restoreFocusTo,
}: {
  read: ReadRule
  names: Names
  onClose: () => void
  onDone: (outcome: 'deleted' | 'paused') => void
  /** A surviving element: the row, and the builder's Delete, are gone by close. */
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  const remove = useDeleteRule()
  const update = useUpdateRule()
  const toast = useToast()
  const { id, name, is_enabled: enabled } = read.rule

  return (
    <ConfirmDialog
      open
      mode="delete_rule"
      title={`Delete “${name}”?`}
      body={
        <>
          <span>{BODY}</span>{' '}
          {read.editable ? (
            <>
              <span>
                When <WhenSentence draft={read.draft} names={names} />
              </span>
              {' · '}
              <span>Then {read.draft.actions.map((a) => actionText(a, names)).join(', ')}</span>
            </>
          ) : (
            <span>{readOnlyNote(read)}</span>
          )}
        </>
      }
      cancelLabel="Keep it"
      confirmLabel="Delete rule"
      pendingLabel="Deleting…"
      destructive
      restoreFocusTo={restoreFocusTo}
      onCancel={onClose}
      onConfirm={async () => {
        await remove.mutateAsync(id)
        toast.show({ kind: 'success', message: `Deleted “${name}”` })
        onDone('deleted')
      }}
      alternative={
        enabled
          ? {
              label: 'Pause instead',
              pendingLabel: 'Pausing…',
              onAct: async () => {
                const paused = await update.mutateAsync({ id, body: { is_enabled: false } })
                emit('rule_paused', { apply_to_past_enabled: paused.rule.rule.apply_to_past })
                toast.show({ kind: 'success', message: `Paused “${name}”` })
                onDone('paused')
              },
            }
          : undefined
      }
      errorFor={() => "Couldn't do that. Nothing was changed. Try again in a moment."}
    />
  )
}
